import { useEffect, useMemo } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowRight,
  Camera,
  Settings2,
  Search,
  ShieldCheck,
  Sparkles,
} from 'lucide-react';
import { api } from '@/lib/api';
import { ROUTES } from '@/lib/routes';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function CommandPalette({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const positions = useQuery({ queryKey: ['positions'], queryFn: api.positions });
  const rules = useQuery({ queryKey: ['rules'], queryFn: api.rules });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onOpenChange]);

  const allRules = useMemo(() => rules.data ?? [], [rules.data]);
  const allPositions = useMemo(() => positions.data ?? [], [positions.data]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-zinc-950/80 backdrop-blur-sm flex items-start justify-center pt-[14vh]"
      onClick={() => onOpenChange(false)}
    >
      <div
        className="w-full max-w-xl rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl shadow-black/50 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Command className="text-zinc-100" loop>
          <div className="flex items-center gap-2 px-3 border-b border-zinc-800">
            <Search className="size-4 text-zinc-400" />
            <Command.Input
              placeholder="Search positions, rules, actions"
              className="flex-1 bg-transparent py-3 outline-none text-sm placeholder:text-zinc-400"
              autoFocus
            />
          </div>
          <Command.List className="max-h-[420px] overflow-y-auto py-2">
            <Command.Empty className="px-4 py-6 text-sm text-zinc-400">
              No matches.
            </Command.Empty>

            <Command.Group heading="Navigate" className="px-2 [&>div]:px-2 [&>div]:text-[11px] [&>div]:uppercase [&>div]:tracking-wider [&>div]:text-zinc-400 [&>div]:py-1.5">
              {ROUTES.map((n) => (
                <Command.Item
                  key={n.to}
                  value={`nav ${n.label}`}
                  onSelect={() => {
                    onOpenChange(false);
                    navigate({ to: n.to });
                  }}
                  className="flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer aria-selected:bg-indigo-500/15 aria-selected:text-indigo-100"
                >
                  <ArrowRight className="size-4 text-zinc-400" />
                  {n.label}
                </Command.Item>
              ))}
            </Command.Group>

            {allPositions.length > 0 && (
              <Command.Group heading="Open positions" className="px-2 [&>div]:px-2 [&>div]:text-[11px] [&>div]:uppercase [&>div]:tracking-wider [&>div]:text-zinc-400 [&>div]:py-1.5">
                {allPositions.map((p) => (
                  <Command.Item
                    key={p.instrument.ticker}
                    value={`pos ${p.instrument.ticker} ${p.instrument.name}`}
                    onSelect={() => {
                      onOpenChange(false);
                      navigate({
                        to: '/positions/$ticker',
                        params: { ticker: p.instrument.ticker },
                      });
                    }}
                    className="flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer aria-selected:bg-indigo-500/15"
                  >
                    <Sparkles className="size-4 text-zinc-400" />
                    <span className="font-mono text-zinc-400 text-xs">{p.instrument.ticker.split('_')[0]}</span>
                    <span className="text-zinc-200">{p.instrument.name}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {allRules.length > 0 && (
              <Command.Group heading="Toggle rules" className="px-2 [&>div]:px-2 [&>div]:text-[11px] [&>div]:uppercase [&>div]:tracking-wider [&>div]:text-zinc-400 [&>div]:py-1.5">
                {allRules.map((r) => (
                  <Command.Item
                    key={r.id}
                    value={`rule ${r.id} ${r.name}`}
                    onSelect={async () => {
                      const result = await api.patchRule(r.id, { enabled: !r.enabled });
                      toast.success(`${result.name} ${result.enabled ? 'enabled' : 'disabled'}`);
                      qc.invalidateQueries({ queryKey: ['rules'] });
                      qc.invalidateQueries({ queryKey: ['violations'] });
                      onOpenChange(false);
                    }}
                    className="flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer aria-selected:bg-indigo-500/15"
                  >
                    <ShieldCheck className="size-4 text-zinc-400" />
                    <span>{r.enabled ? 'Disable' : 'Enable'}: {r.name}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            <Command.Group heading="Actions" className="px-2 [&>div]:px-2 [&>div]:text-[11px] [&>div]:uppercase [&>div]:tracking-wider [&>div]:text-zinc-400 [&>div]:py-1.5">
              <Command.Item
                value="action take snapshot"
                onSelect={async () => {
                  toast.promise(api.takeSnapshot(), {
                    loading: 'Taking snapshot',
                    success: 'Snapshot recorded',
                    error: 'Snapshot failed',
                  });
                  qc.invalidateQueries({ queryKey: ['snapshots'] });
                  onOpenChange(false);
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer aria-selected:bg-indigo-500/15"
              >
                <Camera className="size-4 text-zinc-400" />
                Take snapshot now
              </Command.Item>
              <Command.Item
                value="action settings"
                onSelect={() => {
                  onOpenChange(false);
                  navigate({ to: '/settings' });
                }}
                className="flex items-center gap-2 px-3 py-2 rounded-md text-sm cursor-pointer aria-selected:bg-indigo-500/15"
              >
                <Settings2 className="size-4 text-zinc-400" />
                Open settings
              </Command.Item>
            </Command.Group>
          </Command.List>
        </Command>
      </div>
    </div>
  );
}
